import { action, computed, observable, makeObservable } from 'mobx';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { CourseId, CourseShowView } from '../../../types/courses';
import { CourseApis } from '../../../apis/courses';
import { SectionId, SectionShowView, SectionSuccinctIndexView } from '../../../types/sections';
import { DialogState } from '../../../../commonv3/states/dialogState';
import { LessonId, LessonIndexView } from '../../../types/lessons';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import { SectionEventApis } from '../../../apis/sectionEvents';
import { SectionEventType } from '../../../../commonv3/types/section_events';
import { LessonState } from './LessonState';
import { TokenType } from '../../../../commonv3/types/tokens';
import { CourseExamShowView, ExamSectionIndexView } from '../../../types/courseExams';
import { ExamSectionType } from '../../../../commonv3/types/courseExams';
import { EventCategory, SheEventClient } from '../../../../commonv3/events/SheEventClient';

export class CourseShowPageState {
  courseId: CourseId = null;
  selectedLessonId: LessonId = null;
  preview = false;
  course: RemoteResourceV3<CourseShowView> = RemoteResourceV3.loading();
  lessonStates: LessonState[] = [];
  section: RemoteResourceV3<SectionShowView> = RemoteResourceV3.loading();
  sectionDialog: DialogState = new DialogState(false);
  lessonActivationDialog: DialogState = new DialogState(false);

  constructor() {
    makeObservable(this, {
      courseId: observable,
      selectedLessonId: observable,
      preview: observable,
      course: observable,
      lessonStates: observable,
      section: observable,
      sectionDialog: observable,
      lessonActivationDialog: observable,
      init: action.bound,
      reloadCourse: action.bound,
      startLesson: action.bound,
      startFirstLesson: action.bound,
      openActivationDialog: action.bound,
      activateAndStart: action.bound,
      startOrResume: action.bound,
      reportSectionEvent: action.bound,
      handleSectionCompleteButtonClick: action.bound,
      goToSection: action.bound,
      loading: computed,
      courseTitle: computed,
      startExam: action.bound,
      startExamSection: action.bound,
    });
  }

  async init(courseId: CourseId, preview: boolean) {
    this.courseId = courseId;
    this.preview = preview;
    await this.reloadCourse();
    await GlobalStatesV3.currentUser.reloadUser();
  }

  async reloadCourse() {
    const course = await updateRemoteResourceV3(
      this.course,
      (v) => (this.course = v),
      () => CourseApis.show(this.courseId, this.preview)
    );
    if (course) {
      this.lessonStates = course.lessons.map((lesson) => new LessonState(lesson));
    } else {
      this.lessonStates = [];
    }
  }

  async startLesson(lessonId: LessonId) {
    const lessonState = this.getLessonState(lessonId);
    if (!lessonState.isActivated) {
      if (
        lessonState.requiredTokenAmount > 0 &&
        !GlobalStatesV3.currentUser.availableTokens.isUnlimited(TokenType.ONLINE_LESSON)
      ) {
        this.openActivationDialog(lessonId);
      } else {
        await this.activateAndStart(lessonId);
      }
    } else {
      await this.startOrResume(lessonId);
    }
  }

  async startFirstLesson() {
    if (this.lessonStates.length === 0) {
      return;
    }
    await this.startLesson(this.lessonStates[0].lesson.id);
  }

  openActivationDialog(lessonId: LessonId) {
    this.selectedLessonId = lessonId;
    this.lessonActivationDialog.open();
  }

  async activateAndStart(lessonId: LessonId) {
    await this.getLessonState(lessonId).activate();
    await this.startOrResume(lessonId);
    this.lessonActivationDialog.close();
  }

  async startOrResume(lessonId: LessonId) {
    const lesson = this.getLesson(lessonId);
    if (!lesson) {
      return;
    }
    if (lesson.last_opened_section_uid) {
      const section = lesson.sections.find((s) => s.uid === lesson.last_opened_section_uid);
      if (section) {
        GlobalStatesV3.routing.push(V3Routes.courseSection(this.courseId, lessonId, section.id));
        return;
      }
    }
    if (lesson.sections.length > 0) {
      GlobalStatesV3.routing.push(V3Routes.courseSection(this.courseId, lessonId, lesson.sections[0].id));
    }
  }

  async reportSectionEvent(lessonId: LessonId, sectionId: SectionId, eventType: SectionEventType) {
    const lessonState = this.getLessonState(lessonId);
    const section = this.getSection(lessonId, sectionId);
    if (lessonState && section) {
      await SectionEventApis.create(sectionId, eventType);
      this.getLessonState(lessonId).onSectionEvent(sectionId, section.uid, eventType);
    }
  }

  getNextSection(lessonId: LessonId, sectionId: SectionId): [LessonIndexView, SectionSuccinctIndexView] {
    const lesson = this.getLesson(lessonId);
    if (!lesson) {
      return null;
    }
    const currentSectionIndex = lesson.sections.findIndex((section) => section.id === sectionId);
    if (currentSectionIndex >= 0 && lesson.sections.length - 1 > currentSectionIndex) {
      return [lesson, lesson.sections[currentSectionIndex + 1]];
    }
    let currentLessonIndex = this.lessonStates.findIndex((lesson) => lesson.lesson.id === lessonId);
    while (currentLessonIndex >= 0 && currentLessonIndex < this.lessonStates.length - 1) {
      const nextLesson = this.lessonStates[currentLessonIndex + 1].lesson;
      if (nextLesson.sections.length > 0) {
        return [nextLesson, nextLesson.sections[0]];
      }
      currentLessonIndex += 1;
    }
    return null;
  }

  /**
   * Handles the button click event
   * Dispatch tracking events and moving on to the next section
   * @param lessonId
   * @param sectionId
   */
  async handleSectionCompleteButtonClick(lessonId: LessonId, sectionId: SectionId) {
    const lesson = this.getLesson(lessonId);
    if (!lesson) {
      return null;
    }
    this.reportSectionEvent(lessonId, sectionId, SectionEventType.SECTION_COMPLETED);
    const next = this.getNextSection(lessonId, sectionId);
    const baseLogParams = {
      course_id: this.courseId,
      course_title: this.courseTitle,
      lesson_id: lessonId,
      lesson_index: lesson.index,
      lesson_title: lesson.title,
      section_id: sectionId,
    };
    if (next) {
      if (next[0].id !== lessonId) {
        SheEventClient.reportEvent(EventCategory.COURSE, 'lesson_complete_button_click', {
          ...baseLogParams,
          next_lesson_activated: next[0].activated,
        });
      } else {
        SheEventClient.reportEvent(EventCategory.COURSE, 'section_complete_button_click', baseLogParams);
      }
      await this.goToSection(next[0].id, next[1].id);
    } else {
      SheEventClient.reportEvent(EventCategory.COURSE, 'course_complete_button_click', baseLogParams);
      GlobalStatesV3.routing.push(V3Routes.course(this.courseId));
    }
  }

  async goToSection(lessonId: LessonId, sectionId: SectionId) {
    const lesson = this.getLesson(lessonId);
    if (!lesson) {
      return null;
    }
    if (!lesson.activated) {
      if (
        lesson.required_token_amount == 0 ||
        GlobalStatesV3.currentUser.availableTokens.isUnlimited(TokenType.ONLINE_LESSON)
      ) {
        await this.getLessonState(lessonId).activate();
      } else {
        GlobalStatesV3.routing.push(V3Routes.course(this.courseId));
        this.openActivationDialog(lessonId);
        return;
      }
    }
    GlobalStatesV3.routing.push(V3Routes.courseSection(this.courseId, lessonId, sectionId));
  }

  get loading() {
    return this.course.loading || GlobalStatesV3.currentUser.loading;
  }

  get courseTitle() {
    return this.course.map((c) => c.title).getOrElse('');
  }

  getLessonState(lessonId: LessonId): LessonState {
    return this.lessonStates.find((l) => l.lesson.id === lessonId);
  }

  getLesson(lessonId: LessonId) {
    const lessonState = this.getLessonState(lessonId);
    return lessonState ? lessonState.lesson : null;
  }

  getSection(lessonId: LessonId, sectionId: SectionId) {
    const lesson = this.getLesson(lessonId);
    if (!lesson) {
      return null;
    }
    return lesson.sections.find((section) => section.id === sectionId);
  }

  startExam(exam: CourseExamShowView) {
    if (exam.sections.length > 0) {
      this.startExamSection(exam.sections[0]);
    }
  }

  startExamSection(section: ExamSectionIndexView) {
    switch (section.section_type) {
      case ExamSectionType.PRACTICAL:
        GlobalStatesV3.routing.push(V3Routes.examPracticalSection(section.id));
        return;
      case ExamSectionType.WRITING:
        GlobalStatesV3.routing.push(V3Routes.examWritingSection(section.id));
        return;
    }
  }
}
